/**
 * The canvas the shell owns: the offscreen 320×200 frame everything is drawn
 * into, the letterboxed blit onto the visible element, and the inverse of that
 * transform for hit-testing. Pulling it out of the route component means the
 * gate screen, the intro and the Main Menu all reach the screen through one
 * path, and the component is left holding only the decisions.
 */

import { currentDevicePixelRatio } from "../../three/view-metrics.ts";
import { LOGICAL_HEIGHT, LOGICAL_WIDTH, type DrawOp } from "./draw-ops.ts";
import {
  clearLogical,
  clearLogicalTransparent,
  createLogicalSurface,
  type LogicalSurface,
  paintOps,
  present,
} from "./canvas-painter.ts";
import {
  shellBackingSize,
  toViewPixels,
  type LogicalPoint,
  type Presentation,
  type ShellViewport,
  toLogical,
} from "./present.ts";
import type { ShellAssets } from "./shell-assets.ts";

export interface ShellSurface {
  readonly logical: LogicalSurface;
  attach(canvas: HTMLCanvasElement | undefined): void;
  canvas(): HTMLCanvasElement | undefined;
  paint(assets: ShellAssets, ops: readonly DrawOp[]): void;
  /** The black frame the gate holds before the fonts have arrived. */
  clear(): void;
  blit(): void;
  logicalPointAt(clientX: number, clientY: number): LogicalPoint | undefined;
  /**
   * Where a logical rect lands on the visible element, in CSS pixels relative
   * to the canvas's own box — what an overlaid 3D inset needs to sit exactly on
   * the well drawn under it. `undefined` before the first blit.
   */
  cssRectOf(rect: LogicalRect): CssRect | undefined;
}

/** A logical-frame rectangle, as `model/shell-screen.ts` writes them. */
export interface LogicalRect {
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
}

/** The same rectangle in CSS pixels, relative to the canvas element. */
export interface CssRect {
  readonly left: number;
  readonly top: number;
  readonly width: number;
  readonly height: number;
}

export interface ShellSurfaceOptions {
  /**
   * Composite the frame over whatever is behind the canvas rather than over
   * black. G38's in-flight `ESC` board is the only surface that wants it: the
   * original draws that dialog over the cockpit, while the three existing
   * in-flight sheets (map, PREFERENCES, debriefing) all deliberately replace the
   * picture instead.
   */
  readonly transparent?: boolean;
}

export function createShellSurface(options: ShellSurfaceOptions = {}): ShellSurface {
  const transparent = options.transparent === true;
  // The *logical* canvas needs the alpha channel too, not only the visible one:
  // an opaque backing store blits a black letterbox over whatever is behind the
  // element, however the target context was created.
  const logical = createLogicalSurface(transparent);
  let target: HTMLCanvasElement | undefined;
  let presentation: Presentation | undefined;

  /**
   * The element's CSS box drives the backing store, so a resize is picked up by
   * the next blit rather than by a listener.
   *
   * **In device pixels**, which is what makes the letterbox below an integer
   * count of *real* pixels rather than of CSS ones. `shellBackingSize` carries
   * the measurement of what that buys and what it does not — briefly, nothing at
   * all on a 1x or 2x display, and an even pixel grid instead of a ragged one at
   * 1.5x. Measured against `devicePixelRatio` rather than
   * `getBoundingClientRect`, because the ratio is the quantity that changes when
   * a window is dragged between monitors and the box is the one that does not.
   *
   * Everything downstream of the returned viewport is therefore in device
   * pixels: `presentation`'s origin, its dest size, and the coordinates
   * `toLogical` expects. The two places that cross back into CSS pixels —
   * `logicalPointAt` and `cssRectOf` — convert explicitly, and say so.
   */
  function viewport(): ShellViewport {
    if (!target) return { width: 320, height: 240 };
    const { width, height } = shellBackingSize(target.clientWidth, target.clientHeight, currentDevicePixelRatio());
    // Guarded because assigning `canvas.width` clears the canvas even when the
    // value is unchanged, and a blit runs on every repaint of every shell
    // screen. `present` fills the whole element before it draws, so skipping the
    // clear cannot leave anything of the previous frame behind.
    if (target.width !== width) target.width = width;
    if (target.height !== height) target.height = height;
    return { width, height };
  }

  function blit(): void {
    if (target) presentation = present(target, logical, viewport(), transparent);
  }

  return {
    logical,
    attach(canvas) {
      target = canvas;
    },
    canvas: () => target,
    paint(assets, ops) {
      // A transparent surface has no background op to cover last frame's board,
      // so it is wiped first; an opaque one is not, because its screen paints
      // its own background and the extra fill would be spent on every repaint.
      if (transparent) clearLogicalTransparent(logical);
      paintOps(logical, assets, ops);
      blit();
    },
    clear() {
      clearLogical(logical);
      blit();
    },
    blit,
    logicalPointAt(clientX, clientY) {
      if (!target || !presentation) return undefined;
      // `getBoundingClientRect` on every event rather than a cached box: it is
      // the one read that makes a click correct immediately after a resize,
      // whether or not anything has repainted yet.
      const view = toViewPixels(target.getBoundingClientRect(), target, clientX, clientY);
      return toLogical(presentation, view.x, view.y);
    },
    cssRectOf(rect) {
      if (!target || !presentation) return undefined;
      // The backing store is sized in device pixels (`viewport()` writes
      // `target.width`), while an overlay is positioned in CSS pixels, so the
      // presentation's numbers are scaled back by the element's own ratio.
      const bounds = target.getBoundingClientRect();
      const ratioX = target.width === 0 ? 1 : bounds.width / target.width;
      const ratioY = target.height === 0 ? 1 : bounds.height / target.height;
      const unitX = (presentation.destWidth / LOGICAL_WIDTH) * ratioX;
      const unitY = (presentation.destHeight / LOGICAL_HEIGHT) * ratioY;
      return {
        left: presentation.originX * ratioX + rect.x * unitX,
        top: presentation.originY * ratioY + rect.y * unitY,
        width: rect.w * unitX,
        height: rect.h * unitY,
      };
    },
  };
}
